import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Heart, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { copy } from '@/copy';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(copy.register.passwordMismatch);
      return;
    }

    setLoading(true);

    try {
      const result = await register(name, email, password);
      if (result.requires_verification) {
        navigate('/verification-pending', { state: { email } });
      } else {
        const userData = await login(email, password);
        toast.success(copy.register.successToast);
        navigate(userData.onboarding_completed ? '/dashboard' : '/onboarding');
      }
    } catch (error) {
      const message = error.response?.data?.detail || copy.register.errorToast;
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex">
      {/* Left side - Image (hidden on mobile) */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1678977978610-7f989f995edd?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-[#1D2D44]/60"></div>
        <div className="relative z-10 flex flex-col justify-center items-center text-center p-12">
          <h2 className="font-serif text-4xl text-white mb-4">{copy.register.heroTitle}</h2>
          <p className="text-white/80 text-lg max-w-md leading-relaxed">
            {copy.register.heroDescription}
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          {/* Back link */}
          <Link 
            to="/" 
            className="inline-flex items-center text-[#424E59] hover:text-[#1D2D44] mb-8 transition-colors"
            data-testid="back-to-home-link"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {copy.nav.backToHome}
          </Link>

          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="font-serif text-2xl text-[#1D2D44]">
                Our Love <Heart className="inline-block w-5 h-5 text-[#C9A84C] mx-1" strokeWidth={1.5} /> Connection
              </h1>
            </Link>
          </div>

          <Card className="border-none shadow-lg rounded-2xl">
            <CardHeader className="text-center pb-2">
              <h2 className="font-serif text-2xl text-[#1D2D44]">{copy.register.title}</h2>
              <p className="text-[#424E59] text-sm mt-2">
                {copy.register.subtitle}
              </p>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#3A4A5A]">{copy.register.nameLabel}</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={copy.register.namePlaceholder}
                    required
                    data-testid="register-name-input"
                    className="bg-[#F5F5F5] border-transparent focus:border-[#B89B5E] focus:ring-[#B89B5E]/20 rounded-xl h-12 px-4"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#3A4A5A]">{copy.register.emailLabel}</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={copy.register.emailPlaceholder}
                    required
                    data-testid="register-email-input"
                    className="bg-[#F5F5F5] border-transparent focus:border-[#B89B5E] focus:ring-[#B89B5E]/20 rounded-xl h-12 px-4"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#3A4A5A]">{copy.register.passwordLabel}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={copy.register.passwordPlaceholder}
                      required
                      data-testid="register-password-input"
                      className="bg-[#F5F5F5] border-transparent focus:border-[#B89B5E] focus:ring-[#B89B5E]/20 rounded-xl h-12 px-4 pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#424E59] hover:text-[#3A4A5A]"
                      data-testid="toggle-password-visibility"
                      aria-label={copy.a11y.togglePasswordVisibility}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-[#424E59] mt-1.5" data-testid="password-helper-text">{copy.register.passwordHelper}</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[#3A4A5A]">{copy.register.confirmPasswordLabel}</Label>
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={copy.register.confirmPasswordPlaceholder}
                    required
                    data-testid="register-confirm-password-input"
                    className="bg-[#F5F5F5] border-transparent focus:border-[#B89B5E] focus:ring-[#B89B5E]/20 rounded-xl h-12 px-4"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="register-submit-btn"
                  className="w-full bg-[#1D2D44] text-[#B89B5E] hover:bg-[#1F1F1F] rounded-full h-12 text-base font-medium shadow-lg transition-all duration-300 hover:scale-[1.01]"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <span className="w-4 h-4 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin mr-2"></span>
                      {copy.register.submitting}
                    </span>
                  ) : (
                    copy.register.submitButton
                  )}
                </Button>
              </form>

              <p className="text-center mt-5 px-2" style={{ fontSize: '12px', color: '#9AA5A6', lineHeight: '1.6' }} data-testid="register-disclaimer">
                {copy.register.disclaimer}
              </p>

              <div className="mt-6 text-center">
                <p className="text-[#424E59] text-sm">
                  {copy.register.hasAccount}{' '}
                  <Link 
                    to="/login" 
                    className="text-[#1D2D44] font-medium hover:text-[#5E7F63] transition-colors"
                    data-testid="go-to-login-link"
                  >
                    {copy.register.signIn}
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
